import { api } from "@/redux/api";

import {
	LinkedinCapabilities,
	LinkedinCapabilitiesSchema,
	UnipileResponse,
	UnipileResponseSchema,
	ValidateUnipileResponse,
	ValidateUnipileResponseSchema,
} from "@/pages/Private/pages/Account/schema/unipile";
import { CampaignAction } from "@/pages/Private/pages/Account/schema/pendingSchedules";

export const unipileApi = api.injectEndpoints({
	endpoints: (builder) => ({
		hasSlotsAvailable: builder.query<boolean, void>({
			query: () => ({
				url: `/unipile/has-slots-available`,
				method: "GET",
			}),
		}),
		slotsAvailable: builder.query<
			{
				slotsAvailable: number;
				slotAmount: number;
				emailsUsed: number;
				emailsAmount: number;
				emailsPaidAmount: number;
				emailsBonusAmount: number;
				hasStripeSubscription: boolean;
				emailBaseAmount: number;
				emailPrice: number;
				emailsPendingAmount: number | null;
				emailsPendingEffectiveAt: string | null;
				linkedinsUsed: number;
				linkedinsAmount: number;
				linkedinsPaidAmount: number;
				linkedinsBonusAmount: number;
				linkedinPrice: number;
				linkedinBaseAmount: number;
				linkedinsPendingAmount: number | null;
				linkedinsPendingEffectiveAt: string | null;
			},
			number | undefined
		>({
			query: (id) => ({
				url: `/unipile/slots-available/${id}`,
				method: "GET",
			}),
			providesTags: ["Slots"],
		}),
		createUnipileSession: builder.mutation<
			UnipileResponse,
			{ type: "linkedin" | "email"; subType?: "primary" | "secondary"; successRedirectUrl?: string }
		>({
			query: (type) => ({
				url: `/unipile`,
				method: "POST",
				body: type,
				responseSchema: UnipileResponseSchema,
			}),
			invalidatesTags: ["Me"],
		}),
		createSecondaryEmailUnipileSession: builder.mutation<
			UnipileResponse,
			{ successRedirectUrl?: string; userId?: number }
		>({
			query: (type) => ({
				url: `/unipile/secondary-email`,
				method: "POST",
				body: type,
				responseSchema: UnipileResponseSchema,
			}),
			invalidatesTags: ["Me"],
		}),
		deleteUnipileProfile: builder.mutation<
			ValidateUnipileResponse,
			{ type: "linkedin" | "email"; campaignActions?: CampaignAction[] }
		>({
			query: ({ type, campaignActions }) => ({
				url: `/unipile`,
				method: "DELETE",
				body: { type, ...(campaignActions ? { campaignActions } : {}) },
				responseSchema: ValidateUnipileResponseSchema,
			}),
			invalidatesTags: ["Me"],
		}),
		reconnectLinkedin: builder.mutation<UnipileResponse, { successRedirectUrl?: string }>({
			query: ({ successRedirectUrl }) => ({
				url: `/unipile/linkedin/reconnect`,
				method: "POST",
				body: { successRedirectUrl: successRedirectUrl || undefined },
				responseSchema: UnipileResponseSchema,
			}),
			invalidatesTags: ["Me"],
		}),
		recheckLinkedinCapabilities: builder.mutation<LinkedinCapabilities, void>({
			query: () => ({
				url: `/unipile/linkedin/recheck-capabilities`,
				method: "POST",
				responseSchema: LinkedinCapabilitiesSchema,
			}),
			invalidatesTags: ["Me", "ProfilesWithLiSn"],
		}),
		recheckUserLinkedinCapabilities: builder.mutation<LinkedinCapabilities, { userId: number }>({
			query: ({ userId }) => ({
				url: `/unipile/${userId}/linkedin/recheck-capabilities`,
				method: "POST",
				responseSchema: LinkedinCapabilitiesSchema,
			}),
			invalidatesTags: ["Users", "ProfilesWithLiSn"],
		}),
	}),
});

export const {
	useCreateUnipileSessionMutation,
	useDeleteUnipileProfileMutation,
	useHasSlotsAvailableQuery,
	useSlotsAvailableQuery,
	useCreateSecondaryEmailUnipileSessionMutation,
	useReconnectLinkedinMutation,
	useRecheckLinkedinCapabilitiesMutation,
	useRecheckUserLinkedinCapabilitiesMutation,
} = unipileApi;
export const unipileApiReducer = unipileApi.reducer;
export const unipileApiMiddleware = unipileApi.middleware;
